import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

// Material UI v4
import { 
  Box, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, 
  Typography, Checkbox, Button, FormControlLabel, TableContainer, 
  TablePagination, TextField, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, Fab
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

// Resources
import { CSVLink } from 'react-csv';
import { requestPaymentsByOfficeId } from '../actions/actions';
import Utils from '../resources/Utils';
import CircleLoading from './CircleLoading';

// ----------------------------------------------------------------------
// Ícono SVG nativo (Sin dependencias extra)
// ----------------------------------------------------------------------
const FilterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
);

// ----------------------------------------------------------------------
// Estilos estandarizados y blindados
// ----------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 1), 
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(1, 2), },
    [theme.breakpoints.up('md')]: { padding: theme.spacing(1, 3), }
  },
  pageTitle: {
    fontWeight: 600,
    marginTop: 0, 
    marginBottom: theme.spacing(2), 
    color: '#333',
    [theme.breakpoints.down('xs')]: { fontSize: '1.25rem', textAlign: 'center' }
  },
  kpiCard: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    [theme.breakpoints.down('xs')]: { alignItems: 'center', textAlign: 'center' }
  },
  filterPaper: {
    padding: '16px 20px', 
    marginBottom: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px', 
    flexWrap: 'wrap', 
    justifyContent: 'flex-end', 
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch', 
      justifyContent: 'center',
      marginTop: theme.spacing(2),
      gap: '12px',
    }
  },
  csvLinkWrapper: {
    textDecoration: 'none',
    [theme.breakpoints.down('sm')]: { width: '100%', display: 'block' }
  },
  tableContainer: {
    borderRadius: '8px 8px 0 0',
    overflowX: 'auto', 
    WebkitOverflowScrolling: 'touch', 
    [theme.breakpoints.down('sm')]: { maxHeight: 'calc(100vh - 250px)' },
    [theme.breakpoints.up('md')]: { maxHeight: 'calc(100vh - 280px)', minHeight: '300px' }
  },
  headerTableCell: {
    fontWeight: 600,
    color: '#333', 
    whiteSpace: 'nowrap',
    padding: '12px 16px', 
  },
  smallCell: {
    whiteSpace: 'nowrap',
    padding: '6px 16px', 
  },
  paginationWrapper: {
    backgroundColor: '#ffffff', 
    borderTop: '1px solid #e0e0e0',
    [theme.breakpoints.down('xs')]: {
      '& .MuiTablePagination-toolbar': {
        minHeight: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center'
      },
      '& .MuiTablePagination-spacer': { display: 'none' },
      '& .MuiTablePagination-caption': { margin: '4px 0' },
      '& .MuiTablePagination-selectRoot': { margin: '4px 0' },
      '& .MuiTablePagination-actions': { marginLeft: 0, marginTop: '8px' }
    }
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(10), 
    right: theme.spacing(3),
    zIndex: 1000,
    boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
  }
}));

const PaymentsTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); 

  const { init } = useSelector((state) => state);
  const payments = init.payments;
  const configData = init.config?.data || {};

  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 5,
    searchByReference: '',
    searchByAutorization: '',
    initialDate: '',
    finalDate: '',
    isMulti: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [openFiltersDialog, setOpenFiltersDialog] = useState(false); 
  
  const isWarning = payments?.status === 'WARNING';
  const rawData = !isWarning && payments?.data?.data ? payments.data.data : [];
  const totalPages = !isWarning && payments?.data?.totalPage ? payments.data.totalPage : 0;
  
  const totalAmount = useMemo(() => {
    if (Utils.isEmpty(rawData)) return "0.00";
    const total = rawData.reduce((acc, payment) => acc + (payment.mp_amount || 0), 0);
    return total.toFixed(2);
  }, [rawData]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(requestPaymentsByOfficeId(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (payments) {
      setIsLoading(false);
    }
  }, [payments]);

  // Handlers
  const updateFiltersAndFetch = (newFilters) => {
    setIsLoading(true);
    setFilters(newFilters);
    dispatch(requestPaymentsByOfficeId(newFilters));
  };

  const handleChangePage = (event, newPage) => {
    updateFiltersAndFetch({ ...filters, pageNumber: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    updateFiltersAndFetch({
      ...filters,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1 
    });
  };

  const handleChangeSearch = (field) => (event) => {
    updateFiltersAndFetch({ ...filters, [field]: event.target.value, pageNumber: 1 });
  };

  const handleChangeDate = (field) => (event) => {
    const value = event.target.value;
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (newFilters.initialDate && newFilters.finalDate) {
      setIsLoading(true);
      dispatch(requestPaymentsByOfficeId(newFilters));
    }
  };

  const handleTexfieldsClear = () => {
    updateFiltersAndFetch({
      ...filters,
      searchByReference: '',
      searchByAutorization: '',
      initialDate: '',
      finalDate: '',
      pageNumber: 1
    });
  };

  const handleChangeMultiPays = () => {
    updateFiltersAndFetch({ ...filters, isMulti: !filters.isMulti, pageNumber: 1 });
  };

  const msgs = Utils.getMessages();
  const date = new Date().toISOString().substring(0, 10);
  const backgroundColor = configData.appBarColor || theme.palette.primary.main;
  const notIsData = isWarning || Utils.isEmpty(rawData);
  const uiPage = filters.pageNumber - 1; 

  const renderFiltersForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <TextField fullWidth variant="outlined" size="small" value={filters.searchByReference} InputLabelProps={{ shrink: true }} label={msgs.paymentsHeaders.reference} onChange={handleChangeSearch('searchByReference')} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField fullWidth variant="outlined" size="small" value={filters.searchByAutorization} InputLabelProps={{ shrink: true }} label={msgs.paymentsHeaders.authorization} onChange={handleChangeSearch('searchByAutorization')} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField fullWidth type="date" variant="outlined" size="small" value={filters.initialDate} InputLabelProps={{ shrink: true }} inputProps={{ max: date }} label={msgs.paymentsMessages.initialDate} onChange={handleChangeDate('initialDate')} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField fullWidth type="date" variant="outlined" size="small" value={filters.finalDate} InputLabelProps={{ shrink: true }} inputProps={{ max: date }} label={msgs.paymentsMessages.finalDate} onChange={handleChangeDate('finalDate')} />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.actionsContainer}>
          <FormControlLabel 
            style={{ margin: 0, alignSelf: isMobile ? 'flex-start' : 'center' }} 
            label={<Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>{msgs.paymentsMessages.paysMulti}</Typography>}
            control={<Checkbox style={{ color: backgroundColor }} checked={filters.isMulti} onChange={handleChangeMultiPays} />} 
          />
          <Button variant="outlined" color="primary" onClick={handleTexfieldsClear} fullWidth={isMobile}>
            {msgs.paymentsMessages.buttonClear}
          </Button>
          <CSVLink data={rawData} filename={msgs.paymentsMessages.title} className={classes.csvLinkWrapper}>
            <Button variant="contained" color="primary" fullWidth={isMobile}>
              {msgs.General.exportCSV}
            </Button>
          </CSVLink>
        </div>
      </Grid>
    </Grid>
  );

  // 🔥 Se extrajo la paginación a una función para poder reutilizarla sin duplicar código
  const renderPagination = (isTop) => (
    <TablePagination
      className={classes.paginationWrapper}
      // Si se dibuja arriba (Móvil) cambiamos la línea gris hacia abajo para separar de las tarjetas
      style={isTop ? { borderTop: 'none', borderBottom: '1px solid #e0e0e0', borderRadius: '8px 8px 0 0' } : {}}
      labelRowsPerPage={isMobile ? "Filas" : <Typography variant="body2">{msgs.pagination.rowsPerPages}</Typography>}
      labelDisplayedRows={({ from, to, count }) => <Typography variant="body2">{from} - {to} {msgs.pagination.of} {count}</Typography>}
      rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50, 100]} 
      component="div"
      count={totalPages}
      rowsPerPage={filters.pageSize}
      page={uiPage}
      backIconButtonText={msgs.pagination.previousPage}
      nextIconButtonText={msgs.pagination.nextPage}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  );

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.pageTitle}>
        {Utils.getMessages().paymentsMessages.title}
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3} style={{ marginBottom: isMobile ? '16px' : '24px' }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper className={classes.kpiCard} style={{ borderLeftColor: backgroundColor }}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              {msgs.paymentsMessages.totalAmount}
            </Typography>
            <Typography variant="h5" style={{ color: '#333', fontWeight: 'bold', marginTop: '4px' }}>
              ${notIsData ? "0.00" : totalAmount}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper className={classes.kpiCard} style={{ borderLeftColor: '#4caf50' }}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
              {msgs.pagination.rows} Encontrados
            </Typography>
            <Typography variant="h5" style={{ color: '#333', fontWeight: 'bold', marginTop: '4px' }}>
              {notIsData ? 0 : totalPages}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {!isMobile && (
        <Paper elevation={0} className={classes.filterPaper}>
          {renderFiltersForm()}
        </Paper>
      )}

      {isMobile && (
        <>
          <Fab 
            className={classes.fab} 
            color="primary"
            onClick={() => setOpenFiltersDialog(true)}
          >
            <FilterIcon />
          </Fab>

          <Dialog 
            open={openFiltersDialog} 
            onClose={() => setOpenFiltersDialog(false)} 
            fullWidth 
            maxWidth="sm"
          >
            <DialogTitle disableTypography style={{ paddingBottom: '8px' }}>
              <Typography variant="h6" style={{ fontWeight: 600, color: '#333' }}>Filtros de Búsqueda</Typography>
            </DialogTitle>
            <DialogContent dividers>
              {renderFiltersForm()}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenFiltersDialog(false)} color="primary" style={{ fontWeight: 'bold' }}>
                Cerrar
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircleLoading mensajeCargando="Cargando datos" />
        </Box>
      ) : notIsData ? (
        <Paper elevation={1} style={{ padding: '40px', textAlign: 'center' }}>
          <Typography variant='h6' color="textSecondary">
            {msgs.paymentsMessages.notData}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          
          {/* 🔥 PAGINACIÓN ARRIBA: Solo visible en celulares */}
          {isMobile && renderPagination(true)}

          {isMobile ? (
            /* 🔥 VISTA DE MÓVIL: Diseño de Tarjetas (Cards) */
            <Box p={2} style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
              {rawData.slice(uiPage * filters.pageSize, (uiPage * filters.pageSize) + filters.pageSize).map((payment, idx) => (
                <Paper key={idx} elevation={2} style={{ padding: '16px', marginBottom: '16px', borderRadius: '12px', borderLeft: `4px solid ${backgroundColor}` }}>
                  
                  {/* Encabezado de la Tarjeta */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#333' }}>
                      Ref: {payment.mp_reference || 'N/A'}
                    </Typography>
                    <Typography variant="caption" style={{ fontWeight: 600, color: String(payment.status || '').toLowerCase() === 'approved' ? '#4caf50' : backgroundColor, textTransform: 'uppercase' }}>
                      {payment.status || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Monto Principal */}
                  <Box mb={2}>
                    <Typography variant="h5" style={{ color: '#333', fontWeight: 'bold' }}>
                      ${payment.mp_amount}
                    </Typography>
                  </Box>

                  {/* Cuerpo de detalles en dos columnas */}
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" display="block"><strong>Orden:</strong> {payment.mp_order}</Typography>
                      <Typography variant="caption" color="textSecondary" display="block"><strong>Auth:</strong> {payment.mp_authorization}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary" display="block"><strong>Oficina ID:</strong> {payment.officeId}</Typography>
                      <Typography variant="caption" color="textSecondary" display="block"><strong>Fecha:</strong> {payment.register_date}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary" display="block"><strong>Respuesta:</strong> {payment.mp_response}</Typography>
                      <Typography variant="caption" color="textSecondary" display="block" style={{ wordBreak: 'break-word' }}>
                        <strong>Descripción:</strong> {payment.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          ) : (
            /* 🔥 VISTA DE ESCRITORIO: Tabla tradicional compacta */
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      msgs.paymentsHeaders.officeId, msgs.paymentsHeaders.noOrden, msgs.paymentsHeaders.reference,
                      msgs.paymentsHeaders.amount, msgs.paymentsHeaders.response, msgs.paymentsHeaders.authorization,
                      msgs.paymentsHeaders.status, msgs.paymentsHeaders.description, msgs.paymentsHeaders.registerDate
                    ].map((header, idx) => (
                      <TableCell 
                        key={idx} 
                        style={{ backgroundColor, padding: '12px 16px' }}
                      >
                        <Typography variant="body2" className={classes.headerTableCell}>{header}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawData
                    .slice(uiPage * filters.pageSize, (uiPage * filters.pageSize) + filters.pageSize)
                    .map((payment, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell className={classes.smallCell}>
                        <Typography variant="body2" color="textPrimary" style={{ fontWeight: 500 }}>{payment.officeId}</Typography>
                      </TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.mp_order}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.mp_reference}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2" style={{ fontWeight: 'bold' }}>${payment.mp_amount}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.mp_response}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.mp_authorization}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.status}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.description}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{payment.register_date}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* 🔥 PAGINACIÓN ABAJO: Solo visible en PC */}
          {!isMobile && renderPagination(false)}

        </Paper>
      )}
    </Box>
  );
};

export default withRouter(PaymentsTable);